import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import GlobalFilters from "@/components/CollapsibleFiltersPanel";
import { useFilters } from "@/hooks/useFilters";
import reports from "@/api/reports";
import { TX_COLORS, colorFor } from "@/lib/reportColors";

export default function Distribucion() {
  const { t } = useTranslation();
  const { filters, setFilters, reset, payload } = useFilters();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [mode, setMode] = useState("donut"); // donut | pie
  const [loading, setLoading] = useState(false);

  // Stringify the filter payload so referential changes don't trigger needless
  // refetches (intentional — payload identity changes on every render).
  const payloadKey = useMemo(() => JSON.stringify(payload), [payload]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    reports
      .distribucion(payload)
      .then((r) => {
        if (!alive) return;
        setItems(r.data?.items || []);
        setTotal(r.data?.total || 0);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // payload tracked indirectly via payloadKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payloadKey]);

  return (
    <div className="space-y-6" data-testid="distribucion-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight" data-testid="distribucion-title">
          {t("distribucion.title")}
        </h1>
        <p className="text-sm text-muted-foreground">{t("distribucion.subtitle")}</p>
      </div>

      <GlobalFilters filters={filters} onChange={setFilters} onReset={reset} />

      <div className="flex items-center gap-2">
        <Button
          variant={mode === "donut" ? "default" : "outline"}
          size="sm"
          onClick={() => setMode("donut")}
          data-testid="distribucion-mode-donut"
        >
          {t("distribucion.donut")}
        </Button>
        <Button
          variant={mode === "pie" ? "default" : "outline"}
          size="sm"
          onClick={() => setMode("pie")}
          data-testid="distribucion-mode-pie"
        >
          {t("distribucion.pie")}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("distribucion.title")} · {total.toLocaleString()} {t("common.total").toLowerCase()}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid items-center gap-6 md:grid-cols-2">
            {items.length === 0 ? (
              <div
                className="col-span-full py-16 text-center text-sm text-muted-foreground"
                data-testid="distribucion-empty"
              >
                {loading ? t("common.loading") : t("common.no_data")}
              </div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={380}>
                  <PieChart>
                    <Pie
                      data={items}
                      dataKey="count"
                      nameKey="transaction_type"
                      cx="50%"
                      cy="50%"
                      innerRadius={mode === "donut" ? 80 : 0}
                      outerRadius={140}
                      paddingAngle={mode === "donut" ? 2 : 0}
                      data-testid="distribucion-chart"
                    >
                      {items.map((d, i) => (
                        <Cell
                          key={i}
                          fill={TX_COLORS[d.transaction_type] || colorFor(d.transaction_type, i)}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(v, n, p) => [
                        `${v.toLocaleString()} (${p.payload.pct}%)`,
                        n,
                      ]}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>

                <div className="space-y-2" data-testid="distribucion-list">
                  {items.map((d, i) => (
                    <div
                      key={d.transaction_type}
                      className="flex items-center gap-3 rounded-lg border border-border/60 px-3 py-2 transition-colors hover:bg-accent/30"
                      data-testid={`distribucion-row-${d.transaction_type.replace(/\s+/g, "_")}`}
                    >
                      <div
                        className="h-3 w-3 rounded-sm"
                        style={{
                          background:
                            TX_COLORS[d.transaction_type] || colorFor(d.transaction_type, i),
                        }}
                      />
                      <div className="flex-1 font-mono text-sm">{d.transaction_type}</div>
                      <Badge variant="outline" className="font-mono">
                        {d.count.toLocaleString()}
                      </Badge>
                      <Badge variant="secondary" className="font-mono text-xs">
                        {d.pct}%
                      </Badge>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
